<template>
  <div>
    <div class="actions row col-lg-12 mb-5 p-0 m-0 justify-content-between">
      <div class="actions-left">
        <!-- Removed calendar view - it doesn't make sense for experiences -->
      </div>
      <div class="actions-right row col-lg-6 row justify-content-end p-0 m-0">
        <button type="button" class="btn btn-primary" @click="addExperience">{{ $t('add') }}</button>
      </div>
    </div>
    
    <!-- List View -->
    <div>
      <SearchBar @search-clicked="searchClicked"></SearchBar>
      <CTableWrapper 
        ref="table" 
        hover 
        striped 
        :caption="$t('experiences')" 
        :items="items" 
        :actions="actions" 
        :pages="pages" 
        :fields="fields" 
        clickable-rows 
        @row-clicked="rowClicked" 
        @action-clicked="actionClicked" 
        @update-requested="updateItemsRequested" 
        :sorter="{external: true}" 
        :sorterValue="sorterValue" 
        @sorter-value-changed="sorterValueChanged" 
        :loading="loading"
      >
      </CTableWrapper>
    </div>
    
    <!-- Modal de confirmação de exclusão -->
    <CModal
      :show.sync="showDeleteModal"
      :title="$t('confirmDelete')"
      color="primary"
    >
      <p>{{ $t('confirmRemove', { name: experienceToDelete && experienceToDelete.name ? experienceToDelete.name : '' }) }}</p>
      <template #footer>
        <CButton @click="showDeleteModal = false" color="secondary">{{ $t('cancel') }}</CButton>
        <CButton @click="confirmDelete" color="primary" :disabled="deleting">
          <CSpinner v-if="deleting" size="sm" class="mr-1"></CSpinner>
          {{ $t('delete') }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import CTableWrapper from "@/controls/PaginatedTable.vue";
import SearchBar from "@/controls/SearchBar.vue";
import router from '@/router';
import InputGetList from "@/classes/InputGetList";
import i18n from '@/helpers/i18n';
import { convertToPascalCase } from "@/helpers/utils.js";

export default {
  components: { CTableWrapper, SearchBar },
  data () {
    return {
      searchText: '',
      itemsPerPage: localStorage.itemsPerPage ?? 10,
      fields: [
        { key: 'name', label: i18n.t('name'), _classes: 'font-weight-bold', sortable: true },
        { key: 'isFeatured', label: i18n.t('experiences.featured'), sortable: true },
        { key: 'position', label: i18n.t('position'), sortable: true },
      ],
      actions: [
        { key: 'view', label: i18n.t('view') },
        { key: 'priceSchedule', label: i18n.t('priceSchedule.title') },
        { key: 'remove', label: i18n.t('remove') }
      ],
      sorterValue: { column: 'name', asc: true },
      loading: false,
      showDeleteModal: false,
      experienceToDelete: null,
      deleting: false
    }
  },
  computed: {
    ...mapState('experiences', ['getExperiencesOutput']),
    experiencesInfo: function() {
      return this.getExperiencesOutput ? this.getExperiencesOutput : null;
    },
    items: function()
    {
      return this.experiencesInfo && this.experiencesInfo.data ? this.experiencesInfo.data : [];
    },
    pages: function()
    {
      return this.experiencesInfo ? this.experiencesInfo.totalPages : 1;
    }
  },
  mounted: function()
  {
    this.updateItemsRequested(1, this.itemsPerPage);
  },
  activated: function()
  {
    // Refresh the list when the component is activated (e.g., returning from add/edit page)
    this.updateItemsRequested(1, this.itemsPerPage);
  },
  methods: {
    ...mapActions('experiences', ['getExperiences']),
    ...mapActions('experiences', ['deleteExperienceById']),
    rowClicked (item, index) {
      this.$router.push({name: "ExperiencesEdit", query: { id: item.id }, params : { name: item.name } });
    },
    actionClicked(item, action){
      switch(action.key)
      {
        case 'view':
        {
          this.$router.push({name: "ExperiencesEdit", query: { id: item.id }, params : { name: item.name } });
          break;
        }
        case 'priceSchedule':
        {
          this.$router.push({name: "ExperiencesPriceSchedule", query: { id: item.id } });
          break;
        }
        case 'remove':
        {
          this.openDeleteModal(item);
          break;
        }
        default:
        {
          throw "The provided action is not supported!"; 
        }
      }
    },
    updateItemsRequested(currentPage, itemsPerPage)
    {
      this.loading = true;
      this.itemsPerPage = itemsPerPage;

      var input = new InputGetList(itemsPerPage, currentPage, this.searchText, this.sorterValue.asc ? 1 : 2, convertToPascalCase(this.sorterValue.column));

      this.getExperiences(input); 
    },
    addExperience () 
    {
      router.push({ name: 'ExperiencesAdd'});
    },
    editExperience(experience) {
      this.$router.push({name: "ExperiencesEdit", query: { id: experience.id }, params : { name: experience.name } });
    },
    searchClicked(searchText)
    {
      this.searchText = searchText;
      this.$refs.table.reset();

      this.updateItemsRequested(1, this.itemsPerPage);
    },
    sorterValueChanged(value)
    {
      this.sorterValue = value;
    },
    openDeleteModal(item) {
      this.experienceToDelete = item;
      this.showDeleteModal = true;
    },
    async confirmDelete() {
      if (!this.experienceToDelete) return;
      
      try {
        this.deleting = true;
        await this.deleteExperienceById(this.experienceToDelete.id);
        
        // Success - close modal and refresh list
        this.showDeleteModal = false;
        this.experienceToDelete = null;
        this.deleting = false;
        
        // Refresh the list
        this.updateItemsRequested(1, this.itemsPerPage);
        
        // Show success message
        this.$store.dispatch('alert/setAlert', {
          type: 'success',
          message: this.$t('experiences.deleteSuccess')
        });
      } catch (error) {
        console.error('Error deleting experience:', error);
        this.deleting = false;
        this.$store.dispatch('alert/setAlert', {
          type: 'error',
          message: this.$t('experiences.deleteError')
        });
      }
    }
  },
  watch:
  {
    sorterValue:
    {
      handler: function(val)
      {
        this.updateItemsRequested(1, this.itemsPerPage);
      },
      deep: true
    },
    getExperiencesOutput: function(val)
    {
      if(val != null)
      {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.actions-left {
  display: flex;
  align-items: center;
}

.btn-group .btn {
  margin-right: 0;
}
</style>

